import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { PlanCode } from './enums/plans';

type getPlanFeaturesParams = {
  code: PlanCode;
  isRegistration?: boolean;
  discountType?: DiscountTypeEnum;
};

type PlanFeatureProps = {
  readonly code: string;
  readonly label: string;
  readonly order: number;
  readonly flag?: string;
  readonly value?: string;
  readonly translationKey?: string;
  readonly customLabel?: string;
};

export type PlanFeatureType = {
  code: string;
  label: string;
  order: number;
  flag?: string;
  value?: string;
  translationKey?: string;
  customLabel?: string;
};

export const PlanFeature = ({
  code,
  label,
  order,
  flag,
  value,
  translationKey,
  customLabel,
}: PlanFeatureProps): PlanFeatureType => ({
  code,
  label,
  order,
  flag,
  value,
  translationKey,
  customLabel,
});

// Free
export const FreeProfile = PlanFeature({
  code: 'free-profile',
  label: 'common:paywall.features.free.plan.freeProfile',
  order: 3,
});

export const IncludedMedia = PlanFeature({
  code: 'included-media',
  label: 'common:paywall.features.free.plan.includedMedia',
  order: 2,
});

export const BrowseRole = PlanFeature({
  code: 'browse-role',
  label: 'common:paywall.features.free.browseRole',
  order: 4,
});

export const MediaRequest = PlanFeature({
  code: 'media-request',
  label: 'common:paywall.features.free.plan.mediaRequest',
  order: 5,
});

export const RecieveNotification = PlanFeature({
  code: 'saved-searches',
  label: 'common:paywall.features.free.notification',
  order: 6,
});
// Essential
export const RoleSubmissions = PlanFeature({
  code: 'role-submissions',
  label: 'common:paywall.features.essential.plan.roleSubmissions',
  order: 1,
});

export const Connect = PlanFeature({
  code: 'connect',
  label: 'common:paywall.features.essential.connect',
  order: 7,
});

export const CreateReals = PlanFeature({
  code: 'createReals',
  label: 'common:paywall.features.essential.createReals',
  order: 8,
});

// Premium
export const NewRoles = PlanFeature({
  code: 'new-roles',
  label: 'common:paywall.features.premium.plan.newRoles',
  order: 0,
});

export const UnlimitedMediaStorage = PlanFeature({
  code: 'unlimited',
  label: 'common:paywall.features.premium.storageUnlimited',
  order: 2,
});

export const CompletedAuditions = PlanFeature({
  code: 'role-tracker',
  label: 'common:paywall.features.premium.completedAuditions',
  order: 9,
});

export const ViewAll = PlanFeature({
  code: 'view-all',
  label: 'common:paywall.features.premium.plan.viewAll',
  order: 10,
});

export const ThriveBenefits = PlanFeature({
  code: 'thrive-benefits',
  label: 'common:paywall.features.premium.plan.thriveBenefits',
  order: 11,
});

export const RoleTracker = PlanFeature({
  code: 'role-tracker',
  label: 'common:paywall.features.premium.roleTracker',
  order: 12,
});

export const UltimateBenefits = PlanFeature({
  code: 'ultimate-benefits',
  label: 'common:paywall.features.ultimate.ultimateBenefits',
  customLabel: 'common:paywall.features.ultimate.additionalBenefits',
  order: 13,
});

export const Social = PlanFeature({
  code: 'social',
  label: 'common:paywall.features.premium.social',
  order: 14,
});

export const UnlimitedSubmissionAndMedia = PlanFeature({
  code: 'media-submissions',
  label: 'common:paywall.features.premium.unlimitedSubmissionsAndMedia',
  order: 15,
});

export const ConnectSocial = PlanFeature({
  code: 'connect-social',
  label: 'common:paywall.features.premium.connectSocial',
  order: 16,
});

export const TalentScout = PlanFeature({
  code: 'talent-scout',
  label: 'common:paywall.features.premium.talentScout',
  order: 16,
});

export const TrackSubmissions = PlanFeature({
  code: 'track-submissions',
  label: 'common:paywall.features.premium.trackSubmissions',
  order: 16,
});

export const BrowseRoleOnCBill = PlanFeature({
  code: 'browse-role-cbill',
  label: 'common:paywall.plan.features.browseRole',
  order: 17,
});

export const ConfirmMediaRequest = PlanFeature({
  code: 'confirm-media-request',
  label: 'common:paywall.features.free.plan.confirmMediaRequest',
  order: 18,
});

const BASE_FEATURES = [FreeProfile, BrowseRole];

const FREE_REPPED_REG_DISCOUNT_FEATURES = [FreeProfile, BrowseRoleOnCBill];

export const FREE_FEATURES = [...BASE_FEATURES, MediaRequest, RecieveNotification];

export const FREE_REGISTRATION_FEATURES = [FreeProfile, IncludedMedia];

export const ESSENTIAL_FEATURES = [RoleSubmissions, Connect];
export const ESSENTIAL_WITH_FREE_FEATURES = [
  ...BASE_FEATURES,
  RoleSubmissions,
  MediaRequest,
  RecieveNotification,
  Connect,
];

export const PREMIUM_FEATURES = [UnlimitedSubmissionAndMedia, ConnectSocial, TalentScout, TrackSubmissions];

export const PREMIUM_REPPED_REG_DISCOUNT_FEATURES = [
  UnlimitedSubmissionAndMedia,
  ConnectSocial,
  TalentScout,
  TrackSubmissions,
  MediaRequest,
  CreateReals,
  ConfirmMediaRequest,
  RecieveNotification,
  ThriveBenefits,
];

export const ULTIMATE_FEATURES = [UltimateBenefits];

export const FULL_FREE_FEATURES = [...FREE_FEATURES];

export const FULL_ESSENTIAL_FEATURES = [
  RoleSubmissions,
  Connect,
  ...BASE_FEATURES,
  MediaRequest,
  CreateReals,
  RecieveNotification,
];

export const FULL_PREMIUM_FEATURES = [
  UnlimitedSubmissionAndMedia,
  ConnectSocial,
  TalentScout,
  TrackSubmissions,
  MediaRequest,
  CreateReals,
  RecieveNotification,
  ThriveBenefits,
];

export const FULL_ULTIMATE_FEATURES = [
  UltimateBenefits,
  ThriveBenefits,
  ViewAll,
  RoleSubmissions,
  Connect,
  FreeProfile,
  BrowseRole,
  MediaRequest,
  RecieveNotification,
  CreateReals,
  CompletedAuditions,
  Social,
];

const getFreePlanKeyFeatures = ({
  isRegistration,
  isReppedRegDiscount,
}: {
  isRegistration: boolean;
  isReppedRegDiscount?: boolean;
}) => {
  if (isReppedRegDiscount) return FREE_REPPED_REG_DISCOUNT_FEATURES;
  return isRegistration ? FREE_REGISTRATION_FEATURES : FREE_FEATURES;
};

export const getPlanFeatures = ({
  code,
  isRegistration,
  discountType,
}: getPlanFeaturesParams): {
  keyFeatures: PlanFeatureType[];
  features: PlanFeatureType[];
  combinedFeatures?: PlanFeatureType[];
} => {
  const isReppedRegDiscount = discountType === DiscountTypeEnum.ReppedRegistrationDiscount;

  const featuresMap = {
    [PlanCode.Free]: {
      keyFeatures: getFreePlanKeyFeatures({ isRegistration, isReppedRegDiscount }),
      features: FULL_FREE_FEATURES,
      combinedFeatures: null,
    },
    [PlanCode.Essential]: {
      keyFeatures: ESSENTIAL_FEATURES,
      features: FULL_ESSENTIAL_FEATURES,
      combinedFeatures: ESSENTIAL_WITH_FREE_FEATURES,
    },
    [PlanCode.Premium]: {
      keyFeatures: isReppedRegDiscount ? PREMIUM_REPPED_REG_DISCOUNT_FEATURES : PREMIUM_FEATURES,
      features: FULL_PREMIUM_FEATURES,
      combinedFeatures: null,
    },
    [PlanCode.Ultimate]: {
      keyFeatures: ULTIMATE_FEATURES,
      features: FULL_ULTIMATE_FEATURES,
      combinedFeatures: null,
    },
  };

  const planFeatures = featuresMap[code] || {
    keyFeatures: [],
    features: [],
    combinedFeatures: null,
  };

  return {
    keyFeatures: planFeatures.keyFeatures || [],
    features: planFeatures.features || [],
    combinedFeatures: planFeatures?.combinedFeatures,
  };
};
